import { MuseTFLite } from "../../MuseTFLite";
import { isChromiumImageBitmap } from "../utils/support";
import {
  Dimensions,
  ImageFit,
  Pipeline,
  WebGL2PipelineType,
} from "../../types";
import { buildWebGL2Pipeline } from "../pipelines/webgl2";
import {
  MuseTransformer,
  TFLiteTransformerOptions,
  InputResizeMode,
} from "./types";

import {
  BLUR_FILTER_RADIUS,
  MASK_BLUR_RADIUS,
  MODEL_NAME,
  TFLITE_LOADER_NAME,
  TFLITE_SIMD_LOADER_NAME,
  WASM_INFERENCE_DIMENSIONS,
} from "../../constants";

let loadingModelPromise: Promise<void> | null = null;

/**
 * @private
 */
export class TFLiteTransformer implements MuseTransformer {
  private static _tflite: MuseTFLite | null = null;

  private _fitType!: ImageFit;
  private _blurFilterRadius: number | null = null;
  protected _backgroundImage: HTMLImageElement | null = null;
  protected _outputCanvas: HTMLCanvasElement | null = null;
  protected _outputContext:
    | CanvasRenderingContext2D
    | WebGL2RenderingContext
    | null = null;
  protected _webgl2Pipeline: ReturnType<typeof buildWebGL2Pipeline> | null =
    null;

  private _assetsPath: string;
  private _currentMask: ImageData | null;
  private _debounce: boolean;
  private _deferInputResize: boolean;
  private _inferenceDimensions: Dimensions = WASM_INFERENCE_DIMENSIONS;
  private _inferenceInputCanvas: OffscreenCanvas | HTMLCanvasElement;
  private _inferenceInputContext:
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D;
  private _inputFrameCanvas: OffscreenCanvas | HTMLCanvasElement;
  private _inputFrameContext:
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D;
  private _inputResizeMode: InputResizeMode;
  // tslint:disable-next-line no-unused-variable
  private _isSimdEnabled: boolean | null;
  private _maskBlurRadius: number;
  private _maskCanvas: OffscreenCanvas | HTMLCanvasElement;
  private _maskContext:
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D;
  private _pipeline: Pipeline;

  constructor(options: TFLiteTransformerOptions) {
    if (typeof options.assetsPath !== "string") {
      throw new Error("assetsPath parameter is missing");
    }
    let assetsPath = options.assetsPath;
    if (assetsPath && assetsPath[assetsPath.length - 1] !== "/") {
      assetsPath += "/";
    }

    this._assetsPath = assetsPath;
    this._debounce =
      typeof options.debounce === "boolean" ? options.debounce : true;
    this._deferInputResize =
      typeof options.deferInputResize === "boolean"
        ? options.deferInputResize
        : false;
    this._inferenceDimensions =
      options.inferenceDimensions! || this._inferenceDimensions;

    this._inputResizeMode =
      typeof options.inputResizeMode === "string"
        ? options.inputResizeMode
        : isChromiumImageBitmap()
        ? "image-bitmap"
        : "canvas";

    this._pipeline = options.pipeline! || Pipeline.WebGL2;
    this._currentMask = null;
    this._isSimdEnabled = null;
    this._inferenceInputCanvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(1, 1)
        : document.createElement("canvas");
    this._inferenceInputContext = this._inferenceInputCanvas.getContext("2d", {
      willReadFrequently: true,
    }) as OffscreenCanvasRenderingContext2D;
    this._inputFrameCanvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(1, 1)
        : document.createElement("canvas");
    this._inputFrameContext = this._inputFrameCanvas.getContext(
      "2d"
    ) as OffscreenCanvasRenderingContext2D;
    this._maskBlurRadius =
      typeof options.maskBlurRadius === "number"
        ? options.maskBlurRadius
        : this._pipeline === Pipeline.WebGL2
        ? MASK_BLUR_RADIUS
        : MASK_BLUR_RADIUS / 2;
    this._maskCanvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(1, 1)
        : document.createElement("canvas");
    this._maskContext = this._maskCanvas.getContext(
      "2d"
    ) as OffscreenCanvasRenderingContext2D;

    if (options.blurFilterRadius) {
      this.blurFilterRadius = options.blurFilterRadius || BLUR_FILTER_RADIUS;
    }
    if (options.backgroundImage) {
      this.backgroundImage = options.backgroundImage;
      this.fitType = options.fitType || ImageFit.Fill;
    }
  }

  /**
   * The current blur radius when smoothing out the edges of the person's mask.
   */
  get maskBlurRadius(): number {
    return this._maskBlurRadius;
  }

  /**
   * Set a new blur radius to be used when smoothing out the edges of the person's mask.
   */
  set maskBlurRadius(radius: number) {
    if (typeof radius !== "number" || radius < 0) {
      console.warn(
        `Valid mask blur radius not found. Using ${MASK_BLUR_RADIUS} as default.`
      );
      radius = MASK_BLUR_RADIUS;
    }
    if (this._maskBlurRadius !== radius) {
      this._maskBlurRadius = radius;
      this._webgl2Pipeline?.updatePostProcessingConfig({
        jointBilateralFilter: {
          sigmaSpace: this._maskBlurRadius,
        },
      });
    }
  }

  /**
   * The current background blur filter radius in pixels.
   */
  get blurFilterRadius(): number | null {
    return this._blurFilterRadius;
  }

  /**
   * Set a new background blur filter radius in pixels.
   */
  set blurFilterRadius(radius: number | null) {
    if (radius === null) {
      this._blurFilterRadius = null;
      this._webgl2Pipeline?.cleanUp();
      this._webgl2Pipeline = null;
      return;
    }
    if (!radius) {
      console.warn(
        `Valid blur filter radius not found. Using ${BLUR_FILTER_RADIUS} as default.`
      );
      radius = BLUR_FILTER_RADIUS;
    }
    this._blurFilterRadius = radius;
  }

  /**
   * The HTMLImageElement representing the current background image.
   */
  get backgroundImage(): HTMLImageElement {
    return this._backgroundImage!;
  }

  /**
   * Set an HTMLImageElement as the new background image.
   * An error will be raised if the image hasn't been fully loaded yet. Additionally, the image must follow
   * [security guidelines](https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image)
   * when loading the image from a different origin. Failing to do so will result to an empty output frame.
   */
  set backgroundImage(image: HTMLImageElement | null) {
    if (!image) {
      this._backgroundImage = null;
      this._webgl2Pipeline?.cleanUp();
      this._webgl2Pipeline = null;
      return;
    }
    if (!image.complete || !image.naturalHeight) {
      throw new Error(
        "Invalid image. Make sure that the image is an HTMLImageElement and has been successfully loaded"
      );
    }
    this._backgroundImage = image;

    // Triggers recreation of the pipeline in the next processFrame call
    this._webgl2Pipeline?.cleanUp();
    this._webgl2Pipeline = null;
  }

  /**
   * The current [[ImageFit]] for positioning of the background image in the viewport.
   */
  get fitType(): ImageFit {
    return this._fitType;
  }

  /**
   * Set a new [[ImageFit]] to be used for positioning the background image in the viewport.
   */
  set fitType(fitType: ImageFit) {
    const validTypes = Object.keys(ImageFit);
    if (!validTypes.includes(fitType as any)) {
      console.warn(
        `Valid fitType not found. Using '${ImageFit.Fill}' as default.`
      );
      fitType = ImageFit.Fill;
    }
    this._fitType = fitType;
  }

  /**
   * Load the segmentation model.
   * Call this method before attaching the processor to ensure
   * video frames are processed correctly.
   */
  async loadModel(): Promise<void> {
    if (!loadingModelPromise) {
      loadingModelPromise = new Promise<void>((resolve) => {
        const tflite = new MuseTFLite();
        tflite.initialize(
          this._assetsPath,
          MODEL_NAME,
          TFLITE_LOADER_NAME,
          TFLITE_SIMD_LOADER_NAME
        ).then(() => {
          TFLiteTransformer._tflite = tflite;
          setTimeout(() => {
              loadingModelPromise = null
              resolve()
            }, 
            1000
          );
        })
      })
    }

    await loadingModelPromise;
    this._isSimdEnabled = TFLiteTransformer._tflite?.isSimdEnabled ?? false;
  }

  //   async init({
  //     outputCanvas,
  //     inputElement: inputVideo,
  //   }: VideoTransformerInitOptions) {}

  //   startTransforming(): void {}

  //   destroy(): void {}

  //   restart(): void {}

  //   update(): void {}

  /**
   * Apply a transform to the background of an input video frame and leaving
   * the foreground (person(s)) untouched. Any exception detected will
   * result in the frame being dropped.
   * @param inputFrameBuffer - The source of the input frame to process.
   * <br/>
   * <br/>
   * [OffscreenCanvas](https://developer.mozilla.org/en-US/docs/Web/API/OffscreenCanvas) - Good for canvas-related processing
   * that can be rendered off screen. Only works when using [[Pipeline.Canvas2D]].
   * <br/>
   * <br/>
   * [HTMLCanvasElement](https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement) - This is recommended on browsers
   * that doesn't support `OffscreenCanvas`, or if you need to render the frame on the screen. Only works when using [[Pipeline.Canvas2D]].
   * <br/>
   * <br/>
   * [HTMLVideoElement](https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement) - Recommended when using [[Pipeline.WebGL2]] but
   * works for both [[Pipeline.Canvas2D]] and [[Pipeline.WebGL2]].
   * <br/>
   * @param outputFrameBuffer - The output frame buffer to use to draw the processed frame.
   */
  async processFrame(
    inputFrameBuffer: OffscreenCanvas | HTMLCanvasElement | HTMLVideoElement,
    outputFrameBuffer: HTMLCanvasElement
  ): Promise<void> {
    if (!TFLiteTransformer._tflite) {
      return;
    }
    if (!inputFrameBuffer || !outputFrameBuffer) {
      throw new Error("Missing input or output frame buffer");
    }

    const { width: inferenceWidth, height: inferenceHeight } =
      this._inferenceDimensions;

    const { width: captureWidth, height: captureHeight } =
      inputFrameBuffer instanceof HTMLVideoElement
        ? {
            width: inputFrameBuffer.videoWidth,
            height: inputFrameBuffer.videoHeight,
          }
        : inputFrameBuffer;

    if (this._outputCanvas !== outputFrameBuffer) {
      this._outputCanvas = outputFrameBuffer;
      this._outputContext = this._outputCanvas.getContext(
        this._pipeline === Pipeline.Canvas2D ? "2d" : "webgl2"
      ) as CanvasRenderingContext2D | WebGL2RenderingContext;
      this._webgl2Pipeline?.cleanUp();
      this._webgl2Pipeline = null;
    }

    if (this._pipeline === Pipeline.WebGL2) {
      if (!this._webgl2Pipeline) {
        this._createWebGL2Pipeline(
          inputFrameBuffer as HTMLVideoElement,
          captureWidth,
          captureHeight,
          inferenceWidth,
          inferenceHeight
        );
      }
      this._webgl2Pipeline?.sampleInputFrame();
    }

    // Only set the canvas' dimensions if they have changed to prevent unnecessary redraw
    if (this._inputFrameCanvas.width !== captureWidth) {
      this._inputFrameCanvas.width = captureWidth;
    }
    if (this._inputFrameCanvas.height !== captureHeight) {
      this._inputFrameCanvas.height = captureHeight;
    }
    if (this._inferenceInputCanvas.width !== inferenceWidth) {
      this._inferenceInputCanvas.width = inferenceWidth;
      this._maskCanvas.width = inferenceWidth;
    }
    if (this._inferenceInputCanvas.height !== inferenceHeight) {
      this._inferenceInputCanvas.height = inferenceHeight;
      this._maskCanvas.height = inferenceHeight;
    }

    let inputFrame: OffscreenCanvas | HTMLCanvasElement | HTMLVideoElement;
    if (inputFrameBuffer instanceof HTMLVideoElement) {
      this._inputFrameContext.drawImage(
        inputFrameBuffer,
        0,
        0,
        inputFrameBuffer.width,
        inputFrameBuffer.height
      );
      inputFrame = this._inputFrameCanvas;
    } else {
      inputFrame = inputFrameBuffer;
    }

    const personMask = await this._createPersonMask(inputFrame);
    if (this._debounce) {
      this._currentMask = this._currentMask === personMask ? null : personMask;
    }

    if (this._pipeline === Pipeline.WebGL2) {
      this._webgl2Pipeline?.render(personMask.data);
    } else {
      if (!this._debounce || this._currentMask) {
        this._maskContext.putImageData(personMask, 0, 0);
      }
      const ctx = this._outputContext as CanvasRenderingContext2D;
      const { height: outputHeight, width: outputWidth } = this._outputCanvas;
      ctx.save();
      ctx.filter = `blur(${this._maskBlurRadius}px)`;
      ctx.globalCompositeOperation = "copy";
      ctx.drawImage(this._maskCanvas, 0, 0, outputWidth, outputHeight);
      ctx.filter = "none";
      ctx.globalCompositeOperation = "source-in";
      ctx.drawImage(inputFrame, 0, 0, outputWidth, outputHeight);
      ctx.globalCompositeOperation = "destination-over";
      this._setBackground(inputFrame);
      ctx.restore();
    }
  }

  protected _getWebGL2PipelineType(): WebGL2PipelineType {
    return this._blurFilterRadius && this._blurFilterRadius > 0
      ? WebGL2PipelineType.Blur
      : WebGL2PipelineType.Image;
  }

  protected _setBackground(
    inputFrame?: OffscreenCanvas | HTMLCanvasElement
  ): void {
    const pipelineType = this._getWebGL2PipelineType();
    if (pipelineType === WebGL2PipelineType.Blur) {
      if (!this._outputContext) {
        return;
      }
      const ctx = this._outputContext as CanvasRenderingContext2D;
      ctx.filter = `blur(${this._blurFilterRadius}px)`;
      ctx.drawImage(inputFrame!, 0, 0);
    } else {
      if (!this._outputContext || !this._outputCanvas) {
        return;
      }
      const img = this._backgroundImage!;
      const imageWidth = img.naturalWidth;
      const imageHeight = img.naturalHeight;
      const canvasWidth = this._outputCanvas.width;
      const canvasHeight = this._outputCanvas.height;
      const ctx = this._outputContext as CanvasRenderingContext2D;

      if (this._fitType === ImageFit.Fill) {
        ctx.drawImage(
          img,
          0,
          0,
          imageWidth,
          imageHeight,
          0,
          0,
          canvasWidth,
          canvasHeight
        );
      } else if (this._fitType === ImageFit.None) {
        ctx.drawImage(img, 0, 0, imageWidth, imageHeight);
      } else if (this._fitType === ImageFit.Contain) {
        const { x, y, w, h } = this._getFitPosition(
          imageWidth,
          imageHeight,
          canvasWidth,
          canvasHeight,
          ImageFit.Contain
        );
        ctx.drawImage(img, 0, 0, imageWidth, imageHeight, x, y, w, h);
      } else if (this._fitType === ImageFit.Cover) {
        const { x, y, w, h } = this._getFitPosition(
          imageWidth,
          imageHeight,
          canvasWidth,
          canvasHeight,
          ImageFit.Cover
        );
        ctx.drawImage(img, 0, 0, imageWidth, imageHeight, x, y, w, h);
      }
    }
  }

  private async _createPersonMask(
    inputFrame: OffscreenCanvas | HTMLCanvasElement
  ): Promise<ImageData> {
    const { height, width } = this._inferenceDimensions;

    if (this._currentMask) {
      return this._currentMask;
    }

    if (!this._deferInputResize) {
      await this._resizeInputFrame(inputFrame);
    }

    const personMaskBuffer = TFLiteTransformer._tflite!.runInference();
    const newMask = new ImageData(personMaskBuffer, width, height);

    return newMask;
  }

  private _createWebGL2Pipeline(
    inputFrame: HTMLVideoElement,
    captureWidth: number,
    captureHeight: number,
    inferenceWidth: number,
    inferenceHeight: number
  ): void {
    this._webgl2Pipeline = buildWebGL2Pipeline(
      {
        htmlElement: inputFrame,
        width: captureWidth,
        height: captureHeight,
      },
      this._backgroundImage,
      {
        type: this._getWebGL2PipelineType(),
      },
      {
        inputResolution: `${inferenceWidth}x${inferenceHeight}`,
      },
      this._outputCanvas!,
      this._debounce
    );
    this._webgl2Pipeline.updatePostProcessingConfig({
      jointBilateralFilter: {
        sigmaSpace: this._maskBlurRadius,
        sigmaColor: 0.1,
      },
      coverage: [0, 0.99],
      lightWrapping: 0,
      blendMode: "screen",
    });
  }

  private async _resizeInputFrame(
    inputFrame: OffscreenCanvas | HTMLCanvasElement
  ): Promise<void> {
    if (inputFrame.width === 0) {
      // Not ready yet
      return
    }
    const {
      _inferenceInputCanvas: { width: resizeWidth, height: resizeHeight },
      _inferenceInputContext: ctx,
      _inputResizeMode: resizeMode,
    } = this;
    if (resizeMode === "image-bitmap") {
      const resizedInputFrameBitmap = await createImageBitmap(inputFrame, {
        resizeWidth,
        resizeHeight,
        resizeQuality: "pixelated",
      });
      ctx.drawImage(resizedInputFrameBitmap, 0, 0, resizeWidth, resizeHeight);
      resizedInputFrameBitmap.close();
    } else {
      ctx.drawImage(inputFrame, 0, 0, resizeWidth, resizeHeight);
    }
    const imageData = ctx.getImageData(0, 0, resizeWidth, resizeHeight);
    TFLiteTransformer._tflite!.loadInputBuffer(imageData.data);
  }

  private _getFitPosition(
    contentWidth: number,
    contentHeight: number,
    viewportWidth: number,
    viewportHeight: number,
    type: ImageFit
  ): { h: number; w: number; x: number; y: number } {
    // Calculate new content width to fit viewport width
    let factor = viewportWidth / contentWidth;
    let newContentWidth = viewportWidth;
    let newContentHeight = factor * contentHeight;

    // Scale down the resulting height and width more
    // to fit viewport height if the content still exceeds it
    if (
      (type === ImageFit.Contain && newContentHeight > viewportHeight) ||
      (type === ImageFit.Cover && viewportHeight > newContentHeight)
    ) {
      factor = viewportHeight / newContentHeight;
      newContentWidth = factor * newContentWidth;
      newContentHeight = viewportHeight;
    }

    // Calculate the destination top left corner to center the content
    const x = (viewportWidth - newContentWidth) / 2;
    const y = (viewportHeight - newContentHeight) / 2;

    return {
      x,
      y,
      w: newContentWidth,
      h: newContentHeight,
    };
  }

  update(options: {
    backgroundImage?: HTMLImageElement;
    blurFilterRadius?: number;
  }) {
    if (options.blurFilterRadius) {
      this.blurFilterRadius = options.blurFilterRadius;
      this.backgroundImage = null;
    }
    if (options.backgroundImage) {
      this.backgroundImage = options.backgroundImage;
      this.fitType = ImageFit.Cover;
      this.blurFilterRadius = null;
    }
  }

  destroy() {
    this._webgl2Pipeline?.cleanUp();
    this._webgl2Pipeline = null;
    this._outputCanvas = null;
    this._outputContext = null;
    TFLiteTransformer._tflite = null;
  }

  restart() {
    this._webgl2Pipeline?.cleanUp();
    this._webgl2Pipeline = null;
    this._outputCanvas = null;
    this._outputContext = null;
    TFLiteTransformer._tflite = null;
  }
}
